<template>
  <div :id="id" class="flex flex-grow" :style="{ width }">
    <div v-if="!horizontal && defaultWidth" class="relative flex">
      <div :style="{ width: `${yAxesWidth}px` }">
        <BarChart
          :key="'y-axes-chart'"
          :options="yAxesOptions"
          :chart-data="chartCollection"
          :style="{ height }"
        />
      </div>
      <div
        :id="`scrollable-${id}`"
        class="relative overflow-x-auto scrollbar-lg"
        :style="{ maxWidth: `calc(${defaultWidth}px - ${yAxesWidth}px)` }"
      >
        <BarChart
          :key="'analytics-bar-chart'"
          :options="chartOptions"
          :chart-data="chartCollection"
          class="relative z-10"
          :style="{ height, width: `calc(${widthChart}px - ${yAxesWidth}px)` }"
        />
        <div
          v-if="percentages.length"
          class="absolute bottom-12 flex items-center justify-between"
          :style="{ width: `calc(${widthChart}px - ${yAxesWidth}px)` }"
        >
          <div class="h-1 w-6.5 flex-shrink-0"></div>
          <div
            v-for="(percentage, idx) in percentages"
            :key="idx"
            class="w-13 flex items-center justify-center flex-shrink-0 bg-slate-300 rounded-full"
          >
            <BlTextBody2 :text="`${percentage}%`" class="text-slate-800" />
          </div>
          <div class="h-1 w-6.5 flex-shrink-0"></div>
        </div>
      </div>
    </div>
    <HorizontalBarChart
      v-if="horizontal"
      :key="'analytics-horizontal-bar-chart'"
      :options="chartOptions"
      :chart-data="chartCollection"
      :style="{ height, width }"
    />
  </div>
</template>

<script>
import BarChart from "../../charts/BarChart";
import HorizontalBarChart from "../../charts/HorizontalBarChart";
import BlTextBody2 from "../../BlTextBody2.vue";

export default {
  name: "AnalyticsBarChart",
  components: { BarChart, HorizontalBarChart, BlTextBody2 },
  props: {
    labels: { type: Array, default: () => ["no label"] },
    datasets: { type: Array, default: () => [] }, // { label, data, color }[]
    percentages: { type: Array, default: () => [] }, // String[]
    stacked: { type: Boolean, default: false },
    horizontal: { type: Boolean, default: false },
    hideXAxes: { type: Boolean, default: false },
    height: { type: String, default: "17.5rem" },
    width: { type: String, default: "35rem" },
    oneBar: { type: Number, default: 72 }
  },
  data() {
    return {
      maxLength: 16,
      yAxesWidth: 36, // px
      defaultWidth: 0 // px
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `analytics-graph-${combination}`;
    },
    widthChart() {
      const labels = this.labels ? this.labels.length : 0;
      const scale = labels * this.oneBar;
      return scale < this.defaultWidth ? this.defaultWidth : scale;
    },
    /** */
    yAxesOptions() {
      const n = this.labels ? this.labels.length : 0;
      let barPercentage = n * this.oneBar > this.defaultWidth ? 0.5 : n / 30;
      if (this.horizontal) barPercentage = n > 5 ? 0.75 : n / 10;
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: { enabled: false },
        layout: { padding: { bottom: 20 } },
        scales: {
          yAxes: [
            {
              stacked: this.stacked,
              gridLines: { display: false, drawBorder: false },
              ticks: {
                fontSize: 12,
                beginAtZero: true,
                userCallback: function(value, index, values) {
                  if (typeof value === "string") {
                    const max = 16;
                    if (value.length > max) {
                      return value.substring(0, max - 2) + "...";
                    }
                    return value;
                  }
                  if (typeof value === "number") {
                    return Math.floor(value) === value ? value : "";
                  }
                }
              },
              afterFit: ctx => {
                if (!this.horizontal) ctx.width = this.yAxesWidth;
              },
              barPercentage
            }
          ],
          xAxes: [
            {
              gridLines: { display: false, drawBorder: false },
              ticks: { display: false, fontSize: 12 },
              barPercentage
            }
          ]
        }
      };
    },
    chartOptions() {
      const n = this.labels ? this.labels.length : 0;
      let barPercentage = n * this.oneBar > this.defaultWidth ? 0.5 : n / 30;
      if (this.horizontal) barPercentage = n > 5 ? 0.75 : n / 10;
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        layout: { padding: { top: 6 } },
        scales: {
          yAxes: [
            {
              display: this.horizontal,
              stacked: this.stacked,
              gridLines: { display: false, drawBorder: false },
              ticks: {
                fontSize: 12,
                display: this.horizontal,
                beginAtZero: true,
                userCallback: function(value, index, values) {
                  if (typeof value === "string") {
                    const max = 16;
                    if (value.length > max) {
                      return value.substring(0, max - 2) + "...";
                    }
                    return value;
                  }
                  if (typeof value === "number") {
                    return Math.floor(value) === value ? value : "";
                  }
                }
              },
              barPercentage
            }
          ],
          xAxes: [
            {
              position: this.horizontal ? "top" : "bottom",
              display: !this.hideXAxes,
              stacked: this.stacked,
              gridLines: { display: false, drawBorder: false },
              ticks: {
                fontSize: 12,
                beginAtZero: true,
                userCallback: function(value, index, values) {
                  if (typeof value === "string") {
                    const max = 16;
                    if (value.length > max) {
                      return value.substring(0, max - 2) + "...";
                    }
                    return value;
                  }
                  if (typeof value === "number") {
                    return Math.floor(value) === value ? value : "";
                  }
                }
              },
              barPercentage
            }
          ]
        }
      };
    },
    /** */
    yAxesCollection() {
      return {
        datasets: this.datasets.map(i => ({
          label: i.label,
          data: i.data,
          backgroundColor: ""
        })),
        labels: this.labels
      };
    },
    chartCollection() {
      return {
        datasets: this.datasets.map(i => ({
          label: i.label,
          data: i.data,
          backgroundColor: i.color
        })),
        labels: this.labels
      };
    }
  },
  watch: {},
  mounted() {
    const el = document.getElementById(this.id);
    if (el) this.defaultWidth = el.offsetWidth;
    setTimeout(() => {
      const scrollableChart = document.getElementById(`scrollable-${this.id}`);
      if (scrollableChart) {
        scrollableChart.scrollTo({
          right: 0,
          left: scrollableChart.scrollWidth,
          behavior: "smooth"
        });
      }
    }, 500);
  },
  methods: {}
};
</script>



// WEBPACK FOOTER //
// src/components/analytics/graphs/BarChart.vue